/**
 * React Query hook for backend orders.
 * 
 * Uses the centralized account store for state management.
 * - Reads orders from store (populated by useAccountSync)
 * - Mutations update store immediately from HTTP response
 * - Also provides raw HTTP orders for backwards compatibility
 */

import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { OrdersApi } from "../api-client";
import {
  type CreateOrderRequest,
  type OrderResponse,
  toBackendOrderRequest,
} from "../types";
import { useAccountStore, type StoreOrder } from "../store";

/** Simplified order type for open orders display (backwards compatible) */
export interface OpenOrder {
  id: string;
  market: string;
  side: "buy" | "sell";
  orderType: "market" | "limit";
  price: number;
  size: number;
  filledSize: number;
  status: string;
  takeProfit: number | null;
  stopLoss: number | null;
}

export const useBackendOrders = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  
  // Get store actions using getState() - stable reference
  const applyMutationResult = useAccountStore.getState().applyMutationResult;
  
  // Read orders from centralized store (raw data)
  const ordersMap = useAccountStore((state) => state.orders);
  const isInitialized = useAccountStore((state) => state.isInitialized);
  const storeLoading = useAccountStore((state) => state.isLoading);

  // Derive the filtered orders list
  const storeOrders = useMemo(() => {
    return Array.from(ordersMap.values())
      .filter(o => o.status === "open" || o.status === "pending" || o.status === "partially_filled");
  }, [ordersMap]);

  // Also fetch raw orders via HTTP for backwards compatibility
  // (some components need the full Order type with snake_case fields)
  const {
    data: httpOrders = [],
    isLoading: httpLoading,
    error,
    refetch: httpRefetch,
  } = useQuery({
    queryKey: ["backend-orders", userId],
    queryFn: () => (userId ? OrdersApi.listOrders() : []),
    enabled: !!userId,
    staleTime: 5000, // Consider data fresh for 5 seconds
    refetchOnWindowFocus: true,
  });

  // Convert store orders to OpenOrder format for backwards compatibility
  // Memoized to ensure consistent reference for React effect dependencies (e.g., chart overlays)
  const openOrders: OpenOrder[] = useMemo(() => 
    storeOrders.map((o) => ({
      id: o.id,
      market: o.market,
      side: o.side,
      orderType: o.orderType,
      price: o.price,
      size: o.size,
      filledSize: o.filledSize,
      status: o.status,
      takeProfit: o.takeProfit,
      stopLoss: o.stopLoss,
    })),
    [storeOrders]
  );

  const placeOrderMutation = useMutation({
    mutationFn: (request: CreateOrderRequest) => OrdersApi.placeOrder(request),
    onSuccess: (response) => {
      // Apply mutation result to store immediately
      applyMutationResult({ 
        order: response.order,
        position: response.position, // Position included for market orders
      });
      
      // Also invalidate queries for any components still using React Query directly
      queryClient.invalidateQueries({ queryKey: ["backend-orders", userId] });
      // Note: backend-positions removed - WebSocket handles position updates from order fills
      queryClient.invalidateQueries({ queryKey: ["backend-account", userId] });
      queryClient.invalidateQueries({ queryKey: ["account-sync", userId] });
    },
  });

  const cancelOrderMutation = useMutation({
    mutationFn: (orderId: string) => OrdersApi.cancelOrder(orderId),
    onSuccess: (order) => {
      // Apply mutation result to store immediately - this updates the UI without refetching
      applyMutationResult({ order });
      // Note: We don't invalidate queries here to avoid UI reload - store update is sufficient
    },
  });

  // Helper to place order with frontend-friendly params
  const placeOrder = async (params: {
    market: string;
    side: "long" | "short";
    size: number;
    price: number;
    orderType: "market" | "limit";
  }): Promise<OrderResponse> => {
    const request = toBackendOrderRequest(
      params.market,
      params.side,
      params.size,
      params.price,
      params.orderType
    );
    return placeOrderMutation.mutateAsync(request);
  };

  // Refetch function - triggers both HTTP and store refresh
  const refetch = async () => {
    await httpRefetch();
    queryClient.invalidateQueries({ queryKey: ["account-sync", userId] });
  };

  return {
    orders: httpOrders, // Raw Order[] for backwards compatibility
    openOrders, // Simplified from store for real-time updates
    // Only show loading if there's a user to load data for
    // Without user: not loading (will show login prompt or empty state)
    isLoading: userId ? (httpLoading || storeLoading || !isInitialized) : false,
    error: error as Error | null,
    refetch,
    placeOrder,
    cancelOrder: cancelOrderMutation.mutate,
    isPlacing: placeOrderMutation.isPending,
    isCancelling: cancelOrderMutation.isPending,
    placeOrderError: placeOrderMutation.error as Error | null,
    isWsConnected: isInitialized, // Store is connected when initialized
  };
};

// Re-export the order type for backwards compatibility
export type { StoreOrder as BackendOrder };
